import React, { useState, useEffect } from 'react';
import { X, Send, Loader2, Sparkles, FileText } from 'lucide-react';
import { safeFetch } from '../../../lib/api';
import { notifyToast } from '../../../lib/toastStore';
import DocumentTemplateMultiSelect from '../../shared/DocumentTemplateMultiSelect';
import { useComposerBody } from '../email/useComposerBody';
import { ComposerBody } from '../email/ComposerBody';
import { plainTextToEmailHtml, stripHtmlToText } from '../email/composerCore';

interface AiDraftEmailModalProps {
  uid: string;
  stageLabel?: string;
  onClose: () => void;
  onSent: () => void;
}

export const AiDraftEmailModal: React.FC<AiDraftEmailModalProps> = ({
  uid,
  stageLabel,
  onClose,
  onSent,
}) => {
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState('');
  const [isAiDraft, setIsAiDraft] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sending, setSending] = useState(false);
  // Document templates to generate from this applicant's application
  // when the queued email is delivered (Slice 3).
  const [documentTemplateIds, setDocumentTemplateIds] = useState<string[]>([]);

  // Shared WYSIWYG composer — the AI draft arrives as plain text and is
  // paragraph-wrapped into HTML so the admin edits rich text, never raw
  // markup (an HTML source view remains one toggle away).
  const composer = useComposerBody({ disabled: loading });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res: any = await safeFetch(`/api/admin/workflow/applicants/${uid}/draft-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        if (cancelled || !res) return;
        setSubject(res.subject || '');
        composer.reset(plainTextToEmailHtml(res.body || ''));
        setIsAiDraft(res.isAiDraft === true);
        setRecipientEmail(res.recipientEmail || '');
      } catch (err: any) {
        if (!cancelled) notifyToast('error', err.message || 'Failed to draft email');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const handleSend = async () => {
    if (!subject.trim() || composer.isEmpty) {
      notifyToast('warning', 'Subject and body are required');
      return;
    }
    setSending(true);
    try {
      await safeFetch(`/api/admin/workflow/applicants/${uid}/send-stage-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: subject.trim(),
          // Plain-text rendering kept for the email log / legacy readers;
          // `bodyHtml` is the rich payload the send path prefers.
          body: stripHtmlToText(composer.bodyHtml),
          bodyHtml: composer.getSendHtml(),
          documentTemplateIds: documentTemplateIds.length > 0 ? documentTemplateIds : undefined,
        }),
      });
      notifyToast('success', 'Email queued for sending');
      onSent();
      onClose();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9300] flex items-center justify-center p-4 bg-slate-900/50" role="dialog" aria-modal="true" aria-label="Draft stage email" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl border border-slate-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <div>
            <h3 className="text-sm font-extrabold text-slate-900">Draft email to applicant</h3>
            {stageLabel && <p className="text-[11px] text-slate-500 font-medium mt-0.5">Stage: {stageLabel}{recipientEmail ? ` · ${recipientEmail}` : ''}</p>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close email composer" className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 py-8 justify-center text-slate-500 text-sm">
              <Loader2 className="h-5 w-5 animate-spin" /> Drafting email…
            </div>
          ) : (
            <>
              <div className="flex items-center gap-1.5">
                {isAiDraft ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wide bg-violet-100 text-violet-700 rounded-full px-2.5 py-1">
                    <Sparkles className="h-3 w-3" /> AI-drafted
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wide bg-slate-100 text-slate-600 rounded-full px-2.5 py-1">
                    <FileText className="h-3 w-3" /> Templated — AI unavailable
                  </span>
                )}
              </div>
              <label className="block">
                <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wide">Subject</span>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-800" />
              </label>
              <div>
                <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wide">Body</span>
                <div className="mt-1">
                  <ComposerBody composer={composer} minHeight={240} placeholder="Write your message here…" />
                </div>
              </div>
              <div className="border-t border-slate-100 pt-3">
                <DocumentTemplateMultiSelect
                  value={documentTemplateIds}
                  onChange={setDocumentTemplateIds}
                  label="Generate & Attach Documents"
                  hint="Generated from this applicant's application when the email is delivered, saved to their Generated Documents, and attached as PDFs."
                  disabled={sending}
                />
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2.5 min-h-[44px] rounded-xl border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-50 cursor-pointer">Cancel</button>
          <button type="button" onClick={handleSend} disabled={sending || loading} className="px-4 py-2.5 min-h-[44px] rounded-xl bg-brand-primary hover:bg-brand-primary-dark disabled:opacity-50 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send now
          </button>
        </div>
      </div>
    </div>
  );
};
